

import { useState, useCallback, useEffect } from "react";
import { toast } from "../../../lib/toast";
import * as topicoService from "../../../services/topico.service";
import type { Topico } from "../../../services/topico.service";
import { LIMITS, ERROR_MESSAGES, getCharCountStatus } from "../../../lib/constants";

interface UseTopicsParams {
  ataId: string;
  initialTopicos: Topico[];
  onUpdate: () => Promise<void>;
}

interface UseTopicsReturn {
  topicos: Topico[];
  selectedTopicId: string | null;
  editingTopicId: string | null;
  editingTopicTitle: string;
  addingNewTopic: boolean;
  newTopicTitle: string;
  setSelectedTopicId: (id: string | null) => void;
  setEditingTopicId: (id: string | null) => void;
  setEditingTopicTitle: (title: string) => void;
  setAddingNewTopic: (adding: boolean) => void;
  setNewTopicTitle: (title: string) => void;
  handleCreateTopic: (title?: string) => Promise<Topico | undefined>;
  handleUpdateTopic: (topicoId: string, title: string) => Promise<void>;
  handleDeleteTopic: (topicoId: string) => Promise<void>;
  handleReorderTopics: (topicos: Topico[]) => Promise<void>;
}

export function useTopics({
  ataId,
  initialTopicos,
  onUpdate,
}: UseTopicsParams): UseTopicsReturn {
  const [topicos, setTopicos] = useState<Topico[]>(initialTopicos);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [editingTopicId, setEditingTopicId] = useState<string | null>(null);
  const [editingTopicTitle, setEditingTopicTitle] = useState("");
  const [addingNewTopic, setAddingNewTopic] = useState(false);
  const [newTopicTitle, setNewTopicTitle] = useState("");

  // Sincronizar tópicos quando initialTopicos mudar
  useEffect(() => {
    setTopicos(initialTopicos);
  }, [initialTopicos]);

  const handleCreateTopic = useCallback(async (title?: string): Promise<Topico | undefined> => {
    const titleToUse = title !== undefined ? title : newTopicTitle;

    if (!titleToUse.trim()) {
      toast.error("Título do tópico é obrigatório. Digite um nome para o tópico.");
      return undefined;
    }

    if (titleToUse.trim().length > LIMITS.TOPIC_TITLE_MAX) {
      toast.error(`Título do tópico excedeu o limite de ${LIMITS.TOPIC_TITLE_MAX} caracteres. Reduza o texto.`);
      return undefined;
    }

    // Verificar limite de tópicos
    if (topicos.length >= LIMITS.MAX_TOPICS_PER_MEETING) {
      toast.error(ERROR_MESSAGES.MAX_TOPICS_REACHED);
      return undefined;
    }

    try {
      const novoTopico = await topicoService.createTopico(ataId, {
        titulo: titleToUse.trim(),
      });

      toast.success("Tópico criado com sucesso.");
      setNewTopicTitle("");
      setAddingNewTopic(false);


      await onUpdate();


      setTimeout(() => setSelectedTopicId(novoTopico.id), 50);
      
      return novoTopico;
    } catch (error: any) {
      const errMsg = error.message || '';
      if (errMsg.includes('conexão') || errMsg.includes('internet')) {
        toast.error('Não foi possível criar o tópico — verifique sua conexão com a internet.');
      } else {
        toast.error(errMsg || 'Não foi possível criar o tópico. Tente novamente.');
      }
      return undefined;
    }
  }, [ataId, newTopicTitle, onUpdate, topicos.length]);

  const handleUpdateTopic = useCallback(
    async (topicoId: string, title: string) => {
      if (!title.trim()) {
        toast.error("Título do tópico é obrigatório. Digite um nome para o tópico.");
        return;
      }

      if (title.trim().length > LIMITS.TOPIC_TITLE_MAX) {
        toast.error(`Título do tópico excedeu o limite de ${LIMITS.TOPIC_TITLE_MAX} caracteres. Reduza o texto.`);
        return;
      }

      try {
        const updatedTopico = await topicoService.updateTopico(topicoId, {
          titulo: title.trim(),
        });

        setTopicos(
          topicos.map((t) => (t.id === updatedTopico.id ? updatedTopico : t))
        );
        setEditingTopicId(null);
        setEditingTopicTitle("");
        toast.success("Tópico atualizado com sucesso.");

      } catch (error: any) {
        toast.error(error.message || "Não foi possível atualizar o tópico. Tente novamente.");
      }
    },
    [topicos]
  );

  const handleDeleteTopic = useCallback(
    async (topicoId: string) => {
      try {
        await topicoService.deleteTopico(topicoId);


        if (selectedTopicId === topicoId) {
          const remainingTopicos = topicos.filter((t) => t.id !== topicoId);
          setSelectedTopicId(
            remainingTopicos.length > 0 ? remainingTopicos[0].id : null
          );
        }

        toast.success("Tópico excluído com sucesso.");


        await onUpdate();
      } catch (error: any) {
        toast.error(error.message || "Não foi possível excluir o tópico. Verifique se ele possui itens vinculados.");
      }
    },
    [topicos, selectedTopicId, onUpdate]
  );

  const handleReorderTopics = useCallback(
    async (reorderedTopicos: Topico[]) => {
      const previousTopicos = topicos;
      setTopicos(reorderedTopicos);


      try {
        await Promise.all(
          reorderedTopicos.map((topico, index) =>
            topicoService.updateTopico(topico.id, { ordem: index })
          )
        );

      } catch (error: any) {
        toast.error("Não foi possível reordenar os tópicos. A ordem anterior foi restaurada.");

        setTopicos(previousTopicos);
      }
    },
    [topicos]
  );

  return {
    topicos,
    selectedTopicId,
    editingTopicId,
    editingTopicTitle,
    addingNewTopic,
    newTopicTitle,
    setSelectedTopicId,
    setEditingTopicId,
    setEditingTopicTitle,
    setAddingNewTopic,
    setNewTopicTitle,
    handleCreateTopic,
    handleUpdateTopic,
    handleDeleteTopic,
    handleReorderTopics,
  };
}

